import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Search, X } from 'lucide-react';
import type { LocationOption } from '../../lib/api';
import { fold, searchPlaces, type PlaceMatch } from '../../lib/placeSearch';

interface PlaceTree {
  place: LocationOption;
  children: PlaceTree[];
}

/**
 * Nests the flat list the API returns.
 *
 * A place whose `parentId` resolves to nothing in this response surfaces at the top
 * rather than vanishing — the `?qrCode=` form returns a single row that names a parent
 * it did not send, and a retired floor is filtered out from under its active rooms. In
 * both cases dropping the row would mean a technician could not report the fault at all,
 * which is the one outcome this screen must never produce.
 */
export function nest(places: LocationOption[]): PlaceTree[] {
  const byId = new Map<string, PlaceTree>(
    places.map((place) => [place.id, { place, children: [] }]),
  );

  const roots: PlaceTree[] = [];
  for (const place of places) {
    const node = byId.get(place.id)!;
    const parent = place.parentId ? byId.get(place.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  return roots;
}

/**
 * The places to open before the operator touches anything.
 *
 * A single building is not a choice, so making someone tap it to get anywhere is a tap
 * that asks nothing. Descends while each level holds exactly one place, and stops the
 * moment there is a decision to make.
 */
function autoOpen(roots: PlaceTree[]): Set<string> {
  const open = new Set<string>();
  let level = roots;
  while (level.length === 1 && level[0]!.children.length > 0) {
    open.add(level[0]!.place.id);
    level = level[0]!.children;
  }
  return open;
}

/**
 * "Where is the fault?" — SPEC.md §5.1.
 *
 * The list used to be every active place in the property, flat and name-sorted. A
 * property of a hundred rooms is a hundred 56px rows, so finding Room 302 meant
 * scrolling past everything alphabetically before it on a 3G phone. Parents now open on
 * demand, which turns that scroll into a few taps.
 *
 * A parent is still selectable in its own right. Faults happen in corridors, lift
 * lobbies and plant rooms, and those are floors and buildings — so the chevron opens and
 * the name selects, as two separate controls. Collapsing must not narrow what can be
 * reported against.
 *
 * Search sits above it because three taps still lose to knowing the number: someone who
 * came to report a fault in 302 should type "302". Browsing and searching are two modes
 * doing one thing each — a query replaces the tree with flat matches, and clearing it
 * hands back the tree exactly as it was left, still open where it was open.
 */
export function LocationPicker({
  places,
  onSelect,
}: {
  places: LocationOption[];
  onSelect: (place: LocationOption) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const roots = useMemo(() => nest(places), [places]);
  const [open, setOpen] = useState<ReadonlySet<string>>(() => autoOpen(roots));

  const toggle = useCallback((id: string) => {
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const rows = useMemo(() => visibleRows(roots, open), [roots, open]);
  const matches = useMemo(() => searchPlaces(places, query), [places, query]);
  // Folded, not trimmed: a query of only spaces or a stray hyphen carries nothing to
  // search for, and must leave the tree alone rather than report no matches.
  const searching = fold(query).length > 0;

  return (
    <>
      <SearchField value={query} onChange={setQuery} />

      {!searching ? (
        <ul className="mt-3 divide-y divide-slate-800 rounded-xl border border-slate-800">
          {rows.map(({ node, depth }) => (
            <PlaceOption
              key={node.place.id}
              node={node}
              depth={depth}
              expanded={open.has(node.place.id)}
              onToggle={() => toggle(node.place.id)}
              onSelect={() => onSelect(node.place)}
            />
          ))}
        </ul>
      ) : matches.length === 0 ? (
        <p role="status" className="mt-4 text-sm text-slate-500">
          {t('report.noMatches', { query: query.trim() })}
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-800 rounded-xl border border-slate-800">
          {matches.map((match) => (
            <MatchOption
              key={match.place.id}
              match={match}
              onSelect={() => onSelect(match.place)}
            />
          ))}
        </ul>
      )}
    </>
  );
}

function SearchField({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const { t } = useTranslation();

  return (
    <div className="mt-4 flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 pl-3">
      <Search className="size-5 shrink-0 text-slate-500" aria-hidden="true" />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={t('report.searchLabel')}
        placeholder={t('report.searchPlaceholder')}
        // Off on all four: a room name is not a sentence, and autocorrect turning
        // "Room101" into a word is a search that silently finds nothing.
        autoCapitalize="off"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
        // `type="search"` earns the searchbox role and a search-shaped keyboard, but
        // Chromium also draws its own cancel button inside the field — which rendered a
        // second X beside ours, one of them a few pixels wide and unreachable in gloves.
        // Keep the semantics, suppress the decoration, and let the 56px button below own
        // the job.
        className="min-h-[56px] min-w-0 flex-1 bg-transparent text-base text-slate-100 outline-none [&::-webkit-search-cancel-button]:hidden"
      />
      {/* Only once there is something to clear — an always-present X is a 56px target
          next to the keyboard that usually does nothing. */}
      {value.length > 0 && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={t('report.searchClear')}
          className="flex min-h-[56px] w-14 shrink-0 items-center justify-center bg-transparent text-slate-400"
        >
          <X className="size-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/**
 * One search hit, with the path to it.
 *
 * The path is the whole reason a flat result list is safe: `Room 101` alone does not say
 * which building it is in, and a technician sent to the wrong one has no way to tell from
 * this screen that it was the wrong one.
 */
function MatchOption({ match, onSelect }: { match: PlaceMatch; onSelect: () => void }) {
  const { t } = useTranslation();
  const { place, path } = match;

  return (
    <li className="flex">
      <button
        type="button"
        onClick={onSelect}
        className="flex min-h-[56px] min-w-0 flex-1 items-center justify-between gap-2 bg-transparent px-4 py-2 text-left"
      >
        <span className="min-w-0">
          <span className="block truncate text-base">{place.name}</span>
          {path.length > 0 && (
            <span className="block truncate text-xs text-slate-500">{path.join(' › ')}</span>
          )}
        </span>
        {place.floor && (
          <span className="shrink-0 text-xs text-slate-500">
            {t('report.floor', { floor: place.floor })}
          </span>
        )}
      </button>
    </li>
  );
}

interface Row {
  node: PlaceTree;
  depth: number;
}

/** Depth-first, descending only into what has been opened. */
function visibleRows(nodes: PlaceTree[], open: ReadonlySet<string>, depth = 0): Row[] {
  return nodes.flatMap((node) => [
    { node, depth },
    ...(open.has(node.place.id) ? visibleRows(node.children, open, depth + 1) : []),
  ]);
}

function PlaceOption({
  node,
  depth,
  expanded,
  onToggle,
  onSelect,
}: {
  node: PlaceTree;
  depth: number;
  expanded: boolean;
  onToggle: () => void;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const { place } = node;
  const childCount = node.children.length;

  return (
    <li className="flex items-center" style={{ paddingLeft: `${depth * 16}px` }}>
      {/*
        Opening a floor and reporting a fault on that floor are different answers to
        different questions, so they are different controls. 56px wide as well as tall:
        this is the one screen a technician uses with gloves on, in a hurry, and a
        mis-tap here selects the wrong place for the whole report.
      */}
      {childCount > 0 ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={t(expanded ? 'report.collapse' : 'report.expand', {
            name: place.name,
            count: childCount,
          })}
          className="flex min-h-[56px] w-14 shrink-0 items-center justify-center bg-transparent text-slate-400"
        >
          <ChevronRight
            className={`size-5 transition-transform ${expanded ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
        </button>
      ) : (
        <span className="w-14 shrink-0" aria-hidden="true" />
      )}

      <button
        type="button"
        onClick={onSelect}
        className="flex min-h-[56px] min-w-0 flex-1 items-center justify-between gap-2 bg-transparent pr-4 text-left text-base"
      >
        <span className="truncate">{place.name}</span>
        {/* A closed parent would otherwise look like somewhere with no rooms in it. */}
        {childCount > 0 && !expanded ? (
          <span className="shrink-0 text-xs text-slate-500">
            {t('report.childCount', { count: childCount })}
          </span>
        ) : (
          place.floor && (
            <span className="shrink-0 text-xs text-slate-500">
              {t('report.floor', { floor: place.floor })}
            </span>
          )
        )}
      </button>
    </li>
  );
}
